<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-medium"
    :title="'通知連絡先登録'"
  >
    <template v-slot:body="{ cancel }">
      <cross-validation-observer
        v-slot="{ handleSubmit }"
        :initial-values="{ notificationMailList: form.notificationMailList }"
        as="div"
      >
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <v-card-text class="pa-0">
            <div class="modal-body">
              <v-container>
                <v-row>
                  <v-col>
                    <app-label-group
                      label="V番号"
                      :value="contractSummary.vnumber"
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col>
                    <div>
                      <app-badge
                        item="お知らせ通知受信"
                        :isInfo="false"
                        :isRequired="false"
                      />
                      <v-radio-group
                        inline
                        v-model="form.receivesNotifications"
                        hide-details
                      >
                        <v-radio label="ON" :value="true" />
                        <v-radio label="OFF" :value="false" />
                      </v-radio-group>
                    </div>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col>
                    <app-badge
                      item="お知らせ受信種別"
                      :isRequired="form.receivesNotifications"
                      :isInfo="false"
                    />
                    <v-checkbox
                      hide-details
                      label="その他お知らせ"
                      v-model="form.receivingType.receivesNotice"
                    >
                    </v-checkbox>
                  </v-col>
                </v-row>
                <cross-validation-provider
                  class="mb-4"
                  :passed="validReceivingTypeRequired"
                  vid="receivingTypeRequired"
                >
                  お知らせ通知受信が「ON」の場合、お知らせ受信種別の「その他お知らせ」にチェックをして下さい。
                </cross-validation-provider>

                <v-row>
                  <v-col>
                    <app-badge
                      item="通知先メールアドレス"
                      :isRequired="true"
                      :isInfo="true"
                      :msg="$filter.message(`info_mail`)"
                    />
                    <validation-array
                      name="notificationMailList"
                      v-slot="{ fields, push, remove }"
                    >
                      <template v-for="(item, index) in fields" :key="item.key">
                        <v-row class="ga-2">
                          <validation-provider
                            :name="`mail_${item.key}`"
                            v-model="form.notificationMailList[index]"
                            label="通知先メールアドレス"
                            :rules="{
                              required: true,
                              is_email: true,
                              max: { length: 127 },
                              duplicate: { list: emailList(index) },
                            }"
                            v-slot="{ componentField, errorMessage, meta }"
                          >
                            <app-form-input
                              v-bind="componentField"
                              :error-messages="errorMessage"
                              :field-meta="meta"
                            />
                          </validation-provider>

                          <v-btn
                            icon="$mdiMinus"
                            size="small"
                            variant="outlined"
                            class="rounded-sm"
                            @click="removeEmail(index, remove)"
                          />
                        </v-row>
                      </template>
                      <v-row justify="end">
                        <v-btn
                          :disabled="form.notificationMailList.length >= 10"
                          icon="$mdiPlus"
                          size="small"
                          variant="outlined"
                          class="rounded-sm"
                          @click="addEmail(push)"
                        />
                      </v-row>
                    </validation-array>
                  </v-col>
                </v-row>
              </v-container>
            </div>
          </v-card-text>

          <v-divider></v-divider>
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn type="submit" color="primary" variant="flat">設定</v-btn>
                <v-btn @click="cancel" variant="outlined" color="primary"
                  >キャンセル</v-btn
                >
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </cross-validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type { NotificationEntity } from "@/apis/NotificationApi";
import { mapState } from "vuex";
import cloneDeep from "lodash/cloneDeep";
import { VueFinalModal } from "vue-final-modal";

export default defineComponent({
  name: "NotificationContactEdit",
  props: {
    notificationContactInfo: {
      type: Object as PropType<NotificationEntity>,
      required: true,
    },
  },
  computed: {
    ...mapState("user", {
      contractSummary: "contractSummary",
    }),
    validReceivingTypeRequired(): boolean {
      return !(
        this.form.receivesNotifications &&
        !this.form.receivingType.receivesNotice
      );
    },
  },
  data() {
    return {
      form: cloneDeep(this.notificationContactInfo),
    };
  },
  methods: {
    /** 重複チェック用メール一覧 */
    emailList(index: number) {
      return this.form.notificationMailList
        ? this.form.notificationMailList.filter((_, num) => num !== index)
        : [];
    },
    addEmail(push: (item: any) => void) {
      const addItem = "";
      this.form.notificationMailList.push(addItem);
      push(addItem);
    },
    removeEmail(index: number, remove: (idx: number) => void) {
      if (this.form.notificationMailList.length > 1) {
        this.form.notificationMailList.splice(index, 1);
        remove(index);
      } else {
        this.form.notificationMailList = [""];
      }
    },
    async submit() {
      await this.$confirm("通知連絡先情報を登録します。よろしいですか？");
      await this.$api.notification.update(this.form);
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
  },
});
</script>

<style lang="scss" scoped>
:deep(.v-checkbox .v-selection-control) {
  min-height: unset;
}
</style>
